import { Query } from '@apollo/client/react/components';
import { removeNulls } from '@tableau/data-structures';
import { DialogPositioningBehavior, DialogType, DialogWidget } from '@tableau/widgets';
import React from 'react';
import { Messages } from '../../../../../gen-src/Localize';
import {
  ColumnDetailsQuery,
  ColumnDetailsQueryVariables,
  ColumnDetailsDocument
} from '../../../../../gen-src/queries';
import {
  ApiRequestsContext,
  FeatureFlagsContext,
  RemoteTypeMappings,
} from '../../../data-catalog-shared';
import { LabelWrapper } from '../Labels/LabelsHookHelper';
import { ColumnActions } from '../TablePlacePage/InlineActionsMenuHelper';
import { RemoteTypeIcon } from '../datagrid/cellFormatters/RemoteTypeIcon';
import { RenderQueryResult } from '../renderQueryResult';
import { getRowHeight } from '../sharedDisplayGrid/LinksDisplayGrid';
import {
  ColumnAndFieldDetailsLabel,
  ColumnAndFieldDetailsProperty,
  ColumnDetailsColumnName,
  columnAndFieldDetailsLabelsStyle,
  ColumnDetailsPane,
  ColumnDetailsProperties,
  ColumnDetailsTypeRow,
  ColumnFieldsWrapper,
  columnFieldsWrapperMaxRows,
  ActionMenuProperties,
} from '../style/ColumnAndFieldDetailsStyles';
import { ColumnDetailsActionsMenu } from './ColumnDetailsActionsMenu';
import { ColumnFieldsQueryGrid } from './ColumnFieldsQueryGrid';
import { ColumnFlowsQueryGrid } from './ColumnFlowsQueryGrid';
import { useColumnInlineDescriptionEditor } from './ColumnInlineDescriptionEditor';
import { useColumnInlineTagEditor } from './ColumnInlineTagEditor';
import { ColumnDetailsDialogData } from './types/ColumnDetailsDialogData';

const dialogWidth = 600;

type ColumnDetailsDialogProps = {
  readonly columnId: string;
  readonly onClose: () => void;
  readonly afterDescriptionAndTagsChange: () => void;
  readonly canSetDescriptionAndTags: boolean;
  readonly column: ColumnDetailsDialogData;
  readonly detailsAction?: ColumnActions;
};

function ColumnDetailsDialog({ column, ...props }: ColumnDetailsDialogProps): JSX.Element | null {
  const renderOnce = React.useRef(false);
  const { customSensitivityLabelsEnabled } = React.useContext(FeatureFlagsContext);

  const descriptionEditor = useColumnInlineDescriptionEditor({
    columnLuid: column.luid,
    columnTableLuid: extractTableLuid(column),
    description: column.description || '',
    afterDescriptionChange: props.afterDescriptionAndTagsChange,
    canEditDescription: props.canSetDescriptionAndTags
  });

  const taggingEditor = useColumnInlineTagEditor({
    columnLuid: column.luid,
    tags: removeNulls(column.tags.map(tag => tag.name)),
    onUpdate: props.afterDescriptionAndTagsChange,
    canEditTags: props.canSetDescriptionAndTags,
    viewOnly: true
  });

  const enterEditModeFromActionsMenu = React.useCallback((action: ColumnActions | undefined) => {
		if (action && !renderOnce.current) {
			switch (action) {
				case ColumnActions.Description: descriptionEditor.startEditing(); break;
				case ColumnActions.Tag: taggingEditor.startEditing(); break;
			}
		}
		renderOnce.current = true;
	}, [renderOnce, descriptionEditor, taggingEditor]);

  React.useEffect(() => {
		enterEditModeFromActionsMenu(props.detailsAction)	
	}, [enterEditModeFromActionsMenu, props.detailsAction]);

  const fieldsCount = column.referencedByFieldsConnection?.totalCount ?? null;
  const flowsCount = column.referencedByFlowColumnInputFieldConnection?.totalCount ?? null;

  return (
    <DialogWidget
      testId='column-details'
      title={Messages.ColumnDetails()}
      type={DialogType.NormalWithTitleBar}
      positioningBehavior={DialogPositioningBehavior.Centered}
      showCloseButton={true}
      showFooter={false}
      isModal={true}
      onCloseRequest={props.onClose}
      handleGlassClick={props.onClose}
      dialogWidth={dialogWidth}
    >
      <ColumnDetailsPane>
        {customSensitivityLabelsEnabled ?
          <ActionMenuProperties>
            <ColumnDetailsActionsMenu 
              columnId={props.columnId}
              column={column}
              canUpdate={props.canSetDescriptionAndTags}
              enterDescriptionEdit={descriptionEditor.startEditing}
              enterTaggingEdit={taggingEditor.startEditing}
            />
          </ActionMenuProperties> : null
        }
        <ColumnDetailsColumnName>{column.name}</ColumnDetailsColumnName>
        <ColumnDetailsTypeRow>
          <RemoteTypeIcon remoteType={column.remoteType} />
          {RemoteTypeMappings[column.remoteType]()}
        </ColumnDetailsTypeRow>
        <LabelWrapper itemInfo={column} category={'all'} isEager={true} kind={'chip'} wrapper={(e) => <span style={columnAndFieldDetailsLabelsStyle}>{e}</span>} />
        <ColumnDetailsProperties>
          {descriptionEditor.element}
          {taggingEditor.element}
          <ColumnAndFieldDetailsProperty>
            <ColumnAndFieldDetailsLabel>{Messages.UsedByFields()}</ColumnAndFieldDetailsLabel>
            <ColumnFieldsWrapper style={{ height: getRowHeight(fieldsCount, columnFieldsWrapperMaxRows) }}>
              <ColumnFieldsQueryGrid
                columnId={props.columnId}
                rowCount={fieldsCount}
              />
            </ColumnFieldsWrapper>
          </ColumnAndFieldDetailsProperty>
          <ColumnAndFieldDetailsProperty>
            <ColumnAndFieldDetailsLabel>{Messages.UsedByFlows()}</ColumnAndFieldDetailsLabel>
            <ColumnFieldsWrapper style={{ height: getRowHeight(flowsCount, columnFieldsWrapperMaxRows) }}>
              <ColumnFlowsQueryGrid
                columnId={props.columnId}
                rowCount={flowsCount}
              />
            </ColumnFieldsWrapper>
          </ColumnAndFieldDetailsProperty>
        </ColumnDetailsProperties>
      </ColumnDetailsPane>
    </DialogWidget>
  );
}

export type ColumnDetailsDialogQueryWrapperProps = {
  readonly columnId: string;
  readonly tableVizportalId: string | null;
  readonly onClose: () => void;
  readonly afterDescriptionAndTagsChange: () => void;
  readonly detailsAction?: ColumnActions;
};

export function ColumnDetailsDialogQueryWrapper({ columnId, tableVizportalId, ...props }: ColumnDetailsDialogQueryWrapperProps): JSX.Element {
  const apiRequests = React.useContext(ApiRequestsContext);
  const { customSensitivityLabelsEnabled } = React.useContext(FeatureFlagsContext);
  const [canSetDescriptionAndTags, setCanSetDescriptionAndTags] = React.useState(false);

  React.useEffect(
    () => {
      apiRequests.canUserSetColumnDescription({ tableVizportalId: tableVizportalId ?? '' })
        .then(r => setCanSetDescriptionAndTags(r))
        .catch(() => { return; });
    },
    [apiRequests, tableVizportalId]
  );

  return (
    <Query<ColumnDetailsQuery, ColumnDetailsQueryVariables>
      query={ColumnDetailsDocument}
      variables={{ columnId: columnId, customSensitivityLabelsEnabled }}
    >
      {result =>
        <RenderQueryResult result={result} renderers={{
          renderData: ({ columns }) => {
            const column = columns?.[0]

            if (!column) {
              return null;
            }

            return (
              <ColumnDetailsDialog
                columnId={columnId}
                onClose={props.onClose}
                afterDescriptionAndTagsChange={props.afterDescriptionAndTagsChange}
                column={column}
                canSetDescriptionAndTags={canSetDescriptionAndTags}
                detailsAction={props.detailsAction}
              />
            )
          }
        }} />
      }
    </Query>
  );
}

function extractTableLuid(column: ColumnDetailsDialogData): string | null {
  if (column.table && column.table.__typename === 'DatabaseTable') {
    return column.table.luid;
  }

  return null;
}
